/**
 * The day — `docs/new-request.md` §10.6, §10.8, §10.9, Boards 7, 8, 13, 16 A.
 *
 * A timeline at 0.92 px a minute: where the person leaves from, each
 * appointment with its distance and drive to the new place, the drives
 * between, the green *New* block that drags, and the gaps — green where the
 * visit fits (*fits here — …*, a click places it), hatched where it cannot
 * (*too short — needs …*). Nothing here measures a drive: the server sent the
 * legs and `placementOf` / `gapsOf` (shared, the server runs them too) say
 * what they mean.
 *
 * `DayTimeline` is One's day; `DayColumn` is one person's 146 px column in
 * Side by side, which says less — no drives, no start — because three of them
 * sit side by side under one time axis.
 */
import { useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactElement } from 'react'
import {
  GRADE_LABELS, MARGIN_MINUTES, clockBare, clockRange, compareOpenings, daySummary, driveWords,
  gapsOf, hourLabel, milesTone, milesWords, minutesWords, placementOf,
  type CalendarPerson, type DayPlan, type Gap, type Leg, type Placement,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { GripGlyph, HouseGlyph, PinGlyph, TruckGlyph } from './glyphs.js'
import { FITS, HATCH, TONE_BADGE, TONE_FILL } from './tones.js'

/** Pixels a minute (§10.6). */
export const PX = 0.92

export interface Axis { from: number; to: number }

const floor30 = (minutes: number): number => Math.floor(minutes / 30) * 30
const ceil30 = (minutes: number): number => Math.ceil(minutes / 30) * 30

/**
 * 7:30 AM to 5:30 PM, or the person's hours ± 30 min where they reach past it
 * (§10.6). Side by side shares one axis and widens it only for what it draws
 * — the blocks — since its columns carry no start or end chip.
 */
export function axisOf(plans: DayPlan[], columns = false): Axis {
  let from = 450
  let to = 1050
  for (const plan of plans) {
    if (columns) {
      for (const stop of plan.stops) {
        from = Math.min(from, floor30(stop.start - 30))
        to = Math.max(to, ceil30(stop.end + 30))
      }
    } else {
      from = Math.min(from, floor30(plan.workStart - 30))
      to = Math.max(to, ceil30(plan.workEnd + 30))
    }
  }
  return { from, to }
}

const at = (axis: Axis, minutes: number): number => (minutes - axis.from) * PX
const snap5 = (minutes: number): number => Math.round(minutes / 5) * 5

/** The hour rules and their labels; `gutter` is how far left the labels sit. */
export function HourLines({ axis, gutter = 46, labelSize = 'text-[10.5px]', inset = 0 }: {
  axis: Axis
  gutter?: number
  labelSize?: string
  /** Side by side's lines start 40 px in, beside their own label column. */
  inset?: number
}) {
  const hours: number[] = []
  for (let hour = Math.ceil(axis.from / 60) * 60; hour <= axis.to; hour += 60) {
    if (hour > axis.from) hours.push(hour)
  }
  return (
    <>
      {hours.map((hour) => (
        <div key={hour}>
          <div className="absolute right-0 border-t border-line-soft" style={{ top: at(axis, hour), left: inset }} />
          <div
            className={cn('absolute text-right font-semibold text-text-faint', labelSize)}
            style={{ top: at(axis, hour) - 7, left: inset ? 0 : -gutter, width: inset ? inset - 6 : gutter - 6 }}
          >
            {hourLabel(hour)}
          </div>
        </div>
      ))}
    </>
  )
}

/** A drive between two things: a dashed rule and *19 min*. */
function Drive({ top, height, text, tone }: {
  top: number
  height: number
  text: string
  tone: 'drive' | 'about' | 'short'
}) {
  return (
    <div className="absolute left-[14px] right-0 flex items-center gap-1.5" style={{ top, height }}>
      <svg width={2} height={height} aria-hidden
        className={cn('shrink-0', tone === 'about' ? 'text-text-muted' : 'text-indigo')}>
        <line x1={1} y1={0} x2={1} y2={height} stroke="currentColor" strokeWidth={2} strokeDasharray="3 3" />
      </svg>
      <span className={cn('inline-flex items-center gap-1 whitespace-nowrap rounded-tag bg-surface-alt px-1',
        'text-[10.5px] font-bold',
        tone === 'short' ? 'text-danger' : tone === 'about' ? 'text-text-muted' : 'text-indigo')}>
        <TruckGlyph />{text}
      </span>
    </div>
  )
}

const driveTone = (leg: Leg): 'drive' | 'about' => (leg.estimated ? 'about' : 'drive')

/** A small dark chip: where the day starts, or where it ends. */
function DayChip({ top, text, dark }: { top: number; text: string; dark?: boolean }) {
  return (
    <div className="absolute left-0 right-0 flex items-center gap-1.5" style={{ top }}>
      <span className={cn('inline-flex h-[18px] items-center gap-[5px] rounded-[5px] px-[7px] text-[10.5px] font-bold',
        dark ? 'bg-toast text-toast-text' : 'bg-line-soft text-text-body')}>
        <HouseGlyph />{text}
      </span>
    </div>
  )
}

/** The best gap the visit could go in, for which way each stop's drive is read. */
function bestGapIndex(plan: DayPlan, minutes: number): number | null {
  const fits = gapsOf(plan, minutes).filter((gap) => gap.fits)
  if (fits.length === 0) return null
  const best = [...fits].sort((a, b) => compareOpenings(
    { grade: a.grade, added: a.added, date: plan.date, earliest: a.earliest },
    { grade: b.grade, added: b.added, date: plan.date, earliest: b.earliest }))[0]!
  return best.index
}

/**
 * The drive a stop's badge says (§10.6): from the stop to the new place for a
 * stop before the visit, from the place to the stop for one after — *before*
 * and *after* the chosen time, or the best gap while none is chosen.
 */
function badgeLeg(plan: DayPlan, stopIndex: number, pivot: number): Leg | null {
  const legs = plan.legs
  if (!legs) return null
  return stopIndex < pivot ? legs.toP[stopIndex + 1] ?? null : legs.fromP[stopIndex] ?? null
}

const about = (gap: Gap): string => (gap.estimated ? 'about ' : '')

export interface NewBlockWords {
  /** The property's name. */
  title: string
  /** *412 Orchard Ridge Dr · backyard patio and fire pit*. */
  sub: string
}

/**
 * One person's day, full size. `chosenStart` is the visit when it is theirs
 * on this day; dragging it calls `onMove` on release, a green gap `onPick`.
 */
export function DayTimeline({
  plan, person, minutes, chosenStart, axis, words, width, onPick, onMove,
}: {
  plan: DayPlan
  person: CalendarPerson
  minutes: number
  chosenStart: number | null
  axis: Axis
  words: NewBlockWords
  /** 262 on the boards; leave it out to fill the width (a phone). */
  width?: number
  onPick: (start: number) => void
  onMove: (start: number) => void
}) {
  const [live, setLive] = useState<number | null>(null)
  const start = live ?? chosenStart
  const placement: Placement | null = useMemo(
    () => (start === null ? null : placementOf(plan, start, minutes)), [plan, start, minutes])
  const gaps = useMemo(() => gapsOf(plan, minutes), [plan, minutes])
  const pivot = placement?.gap ?? bestGapIndex(plan, minutes) ?? plan.stops.length
  const summary = useMemo(() => daySummary(plan, placement), [plan, placement])
  const height = (axis.to - axis.from) * PX
  const legs = plan.legs
  const n = plan.stops.length
  /*
   * Board 8 B: booking over another appointment is allowed, and the day shows
   * the overlap in red — the stop it runs into keeps its own half, so neither
   * hides the other.
   */
  const overlapped = new Set(start === null ? [] : plan.stops
    .filter((stop) => start < stop.end && start + minutes > stop.start).map((stop) => stop.id))
  const overlaps = overlapped.size > 0

  /* ---- dragging the block (snaps to 5 min; a touch waits for a short press) ---- */
  const drag = useRef<{ y0: number; s0: number; armed: boolean; timer: number | null } | null>(null)
  const bounds = { min: axis.from, max: axis.to - minutes }
  const clamp = (value: number) => Math.min(bounds.max, Math.max(bounds.min, value))

  const down = (event: PointerEvent<HTMLDivElement>) => {
    if (chosenStart === null || event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    const touch = event.pointerType === 'touch'
    const state = { y0: event.clientY, s0: chosenStart, armed: !touch, timer: null as number | null }
    if (touch) {
      state.timer = window.setTimeout(() => { state.armed = true; setLive(state.s0) }, 300)
    }
    drag.current = state
  }
  const move = (event: PointerEvent<HTMLDivElement>) => {
    const state = drag.current
    if (!state || !state.armed) return
    setLive(clamp(snap5(state.s0 + (event.clientY - state.y0) / PX)))
  }
  const up = () => {
    const state = drag.current
    if (state?.timer) window.clearTimeout(state.timer)
    drag.current = null
    if (state?.armed && live !== null && live !== state.s0) onMove(live)
    setLive(null)
  }
  const key = (event: KeyboardEvent<HTMLDivElement>) => {
    if (chosenStart === null) return
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
      onMove(clamp(chosenStart + (event.key === 'ArrowUp' ? -5 : 5)))
    }
  }

  const parts: ReactElement[] = []

  /* Where the day starts. */
  parts.push(<DayChip key="leaves" top={at(axis, plan.workStart) - 9} text={person.leaves} dark />)

  /* The appointments, each with its distance and the drive to the new place. */
  plan.stops.forEach((stop, index) => {
    const top = at(axis, stop.start)
    const blockHeight = Math.max(22, (stop.end - stop.start) * PX)
    const tone = milesTone(stop.miles)
    const leg = badgeLeg(plan, index, pivot)
    parts.push(
      <div
        key={`stop-${stop.id}`}
        data-stop={stop.id}
        className={cn('absolute left-0 overflow-hidden rounded-[9px] border border-line bg-surface py-1.5 pl-3.5 pr-2.5',
          'leading-[normal]', overlapped.has(stop.id) ? 'right-[calc(50%+2px)]' : 'right-0')}
        style={{ top, height: blockHeight }}
      >
        <i className={cn('absolute bottom-0 left-0 top-0 block w-[5px]', TONE_FILL[tone])} />
        <div className="flex items-center gap-1.5">
          <span className="text-[11.5px] font-semibold text-text-muted">{clockRange(stop.start, stop.end, true)}</span>
          <span className="flex-1" />
          {stop.miles !== null && (
            <span className={cn('inline-flex h-[18px] items-center gap-1 whitespace-nowrap rounded-[5px] px-1.5',
              'text-[10.5px] font-bold', TONE_BADGE[tone])}>
              <PinGlyph />
              {milesWords(stop.miles)}{leg ? ` · ${driveWords(leg)}` : ''}
            </span>
          )}
        </div>
        <b className="mt-0.5 block truncate text-[12.5px] font-bold text-text">{stop.title}</b>
        <span className="block truncate text-[11px] text-text-muted">{stop.sub}</span>
      </div>,
    )
  })

  /* The gaps between: drives, the green block, green gaps and hatched ones. */
  for (const gap of gaps) {
    const k = gap.index
    const last = k === n
    const top = at(axis, gap.from)
    const span = (gap.to - gap.from) * PX

    if (placement && placement.gap === k && start !== null) {
      const end = start + minutes
      if (gap.leg1) {
        const before = placement.before
        const tight = before && before.spare < MARGIN_MINUTES
        parts.push(
          <Drive key={`d1-${k}`} top={top} height={Math.max(14, gap.leg1.minutes * PX)}
            tone={tight ? 'short' : driveTone(gap.leg1)}
            text={tight && before
              ? `${start - gap.from} min to drive ${gap.leg1.minutes}${before.spare >= 0 ? ` — ${before.spare} min margin` : ''}`
              : driveWords(gap.leg1)} />,
        )
      }
      parts.push(
        <div
          key="new"
          role="slider"
          tabIndex={0}
          aria-label="New site visit — drag to move"
          aria-valuemin={bounds.min}
          aria-valuemax={bounds.max}
          aria-valuenow={start}
          aria-valuetext={clockRange(start, end)}
          data-new-block
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          onKeyDown={key}
          className={cn('absolute right-0 z-[1] flex cursor-grab touch-none select-none flex-col gap-0.5',
            'overflow-hidden rounded-[9px] border-2 border-dashed px-2.5 py-[7px] leading-[normal]',
            overlaps ? 'left-1/2 border-danger bg-danger-bg'
              : 'left-0 border-accent bg-accent-soft shadow-[0_6px_18px_rgb(var(--color-accent)/0.18)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring',
            live !== null && 'cursor-grabbing')}
          style={{ top: at(axis, start), height: minutes * PX }}
        >
          <div className="flex items-center gap-1.5">
            <b className={cn('text-[12.5px] font-bold', overlaps ? 'text-danger' : 'text-accent-dark')}>
              {clockRange(start, end, true)}
            </b>
            <span className={cn('inline-flex h-[18px] items-center whitespace-nowrap rounded-tag px-1.5 text-[10.5px] font-semibold',
              overlaps ? 'bg-danger-bg text-danger' : 'bg-accent-soft text-accent-dark')}>
              New
            </span>
            <span className="flex-1" />
            <span className={cn('inline-flex', overlaps ? 'text-danger' : 'text-accent-dark')}><GripGlyph /></span>
          </div>
          <b className="truncate text-[13px] font-bold text-text">{words.title}</b>
          <span className="truncate text-[11.5px] text-text-body">{words.sub}</span>
        </div>,
      )
      if (gap.leg2) {
        const after = placement.after
        const tight = after && after.spare < MARGIN_MINUTES
        const driveHeight = Math.max(14, gap.leg2.minutes * PX)
        parts.push(
          <Drive key={`d2-${k}`} top={at(axis, end)} height={driveHeight}
            tone={tight ? 'short' : driveTone(gap.leg2)}
            text={tight && after
              ? `${Math.max(0, after.at - end)} min to drive ${gap.leg2.minutes}${after.spare >= 0 ? ` — ${after.spare} min margin` : ''}`
              : driveWords(gap.leg2)} />,
        )
        if (after && after.spare > 0) {
          const freeTop = at(axis, end + gap.leg2.minutes)
          const freeHeight = at(axis, after.at) - freeTop
          if (freeHeight >= 16) {
            parts.push(
              <div key={`free-${k}`} className="absolute left-0 right-0 flex items-center justify-center text-[11px] text-text-faint"
                style={{ top: freeTop, height: freeHeight }}>
                {minutesWords(after.spare)} free
              </div>,
            )
          }
        }
        if (last) {
          parts.push(<DayChip key="back" top={at(axis, end) + driveHeight + 2}
            text={`${person.backAt} ${clockBare(summary.backAt)}`} />)
        }
      }
      continue
    }

    if (!legs || plan.off) continue

    /*
     * After the last stop the day always draws the drive home and *Back at
     * the office <time>* (§10.6, Board 13 A); a gap that fits or is too short
     * starts under that chip rather than in place of it.
     */
    const home = last && n > 0 ? legs.between[k] ?? null : null
    let homeClear = 0
    if (home) {
      const driveHeight = Math.max(14, home.minutes * PX)
      parts.push(<Drive key={`d-${k}`} top={top} height={driveHeight} tone={driveTone(home)} text={driveWords(home)} />)
      parts.push(<DayChip key="back" top={top + driveHeight + 2}
        text={`${person.backAt} ${clockBare(summary.backAt)}`} />)
      homeClear = driveHeight + 24
    }

    if (gap.fits && gap.earliest !== null) {
      /* The day's first gap starts under the *Leaves …* chip, not on top of it. */
      const clear = home ? homeClear : k === 0 ? 24 : 0
      if (span - clear < 24) continue
      parts.push(
        <button
          key={`fits-${k}`}
          type="button"
          data-gap={k}
          onClick={() => onPick(gap.earliest!)}
          className="absolute left-0 right-0 flex items-center justify-center rounded-[9px] border border-dashed border-accent-line px-1 text-center hover:border-accent"
          style={{ top: top + clear, height: Math.max(24, span - clear), background: FITS }}
        >
          <span className="text-[11px] font-bold text-accent-dark">
            fits here — {minutesWords(gap.free)} free, needs {about(gap)}{minutesWords(gap.needs ?? 0)}
          </span>
        </button>,
      )
      continue
    }

    if (gap.free >= minutes && gap.needs !== null) {
      if (span - homeClear < 24) continue
      parts.push(
        <div key={`short-${k}`} className="absolute left-0 right-0 flex items-center justify-center rounded-[9px] px-1 text-center"
          style={{ top: top + homeClear, height: span - homeClear, background: HATCH }}>
          <span className="text-[10.5px] font-semibold text-text-faint">
            too short — needs {about(gap)}{minutesWords(gap.needs)} with drives
          </span>
        </div>,
      )
      continue
    }

    /* A plain drive: from the start to the first stop, between two, or home. */
    const leg = legs.between[k]
    if (!leg || home) continue
    if (last) {
      const driveHeight = Math.max(14, leg.minutes * PX)
      parts.push(<Drive key={`d-${k}`} top={top} height={driveHeight} tone={driveTone(leg)} text={driveWords(leg)} />)
      parts.push(<DayChip key="back" top={top + driveHeight + 2}
        text={`${person.backAt} ${clockBare(summary.backAt)}`} />)
    } else {
      parts.push(<Drive key={`d-${k}`} top={top + 1} height={Math.max(14, span - 4)} tone={driveTone(leg)}
        text={driveWords(leg)} />)
    }
  }

  /* Where the day ends. */
  parts.push(
    <div key="ends" className="absolute left-0 right-0 border-t-[1.5px] border-dashed border-line-strong"
      style={{ top: at(axis, plan.workEnd) }} />,
    <div key="ends-words" className="absolute right-0 text-[10px] font-semibold text-text-faint"
      style={{ top: at(axis, plan.workEnd) + 3 }}>
      {person.firstName}’s day ends {clockBare(plan.workEnd)}
    </div>,
  )

  return (
    <div className="relative ml-[46px]" style={{ width, height }} data-timeline={plan.date}>
      <HourLines axis={axis} />
      {parts}
    </div>
  )
}

/**
 * One person's column in Side by side (Board 7 B): their blocks with the
 * distance, the visit when it is theirs, *fits from …* gaps that book them
 * and *too short* ones, and where their day ends.
 */
export function DayColumn({ plan, person, minutes, chosenStart, axis, title, onPick }: {
  plan: DayPlan
  person: CalendarPerson
  minutes: number
  chosenStart: number | null
  axis: Axis
  title: string
  onPick: (start: number) => void
}) {
  const gaps = useMemo(() => gapsOf(plan, minutes), [plan, minutes])
  const placement = useMemo(
    () => (chosenStart === null ? null : placementOf(plan, chosenStart, minutes)), [plan, chosenStart, minutes])

  return (
    <div className="relative h-full w-[146px]" data-column={person.id}>
      {plan.stops.map((stop) => {
        const tone = milesTone(stop.miles)
        return (
          <div key={stop.id}
            className="absolute left-0 right-0 overflow-hidden rounded-button border border-line bg-surface py-1 pl-[11px] pr-[7px] leading-[normal]"
            style={{ top: at(axis, stop.start), height: Math.max(22, (stop.end - stop.start) * PX - 2) }}>
            <i className={cn('absolute bottom-0 left-0 top-0 block w-1', TONE_FILL[tone])} />
            <span className="text-[10.5px] font-semibold text-text-muted">{clockRange(stop.start, stop.end, true)}</span>
            <b className="block truncate text-[11.5px] font-bold text-text">{stop.title}</b>
            {stop.miles !== null && (
              <span className={cn('mt-px inline-flex h-4 items-center gap-[3px] whitespace-nowrap rounded-tag px-[5px]',
                'text-[10px] font-bold', TONE_BADGE[tone])}>
                <PinGlyph size={9} />{milesWords(stop.miles)}
              </span>
            )}
          </div>
        )
      })}

      {chosenStart !== null && (
        <div
          data-new-block
          className={cn('absolute left-0 right-0 z-[1] overflow-hidden rounded-button border-2 border-dashed border-accent',
            'bg-accent-soft px-2 py-[5px] leading-[normal] shadow-[0_6px_18px_rgb(var(--color-accent)/0.18)]')}
          style={{ top: at(axis, chosenStart), height: minutes * PX }}
        >
          <div className="flex items-center gap-1">
            <b className="text-[11.5px] font-bold text-accent-dark">{clockRange(chosenStart, chosenStart + minutes, true)}</b>
            <span className="flex-1" />
            <span className="inline-flex h-[17px] items-center rounded-tag bg-accent-soft px-1.5 text-[10px] font-semibold text-accent-dark">
              New
            </span>
          </div>
          <b className="mt-0.5 block truncate text-[11.5px] font-bold text-text">{title}</b>
        </div>
      )}

      {plan.legs && !plan.off && gaps.map((gap) => {
        if (placement && placement.gap === gap.index) return null
        const top = at(axis, gap.from) + 3
        const height = (gap.to - gap.from) * PX - 6
        if (gap.fits && gap.earliest !== null && gap.grade && gap.added !== null) {
          return (
            <button key={gap.index} type="button" data-gap={gap.index}
              onClick={() => onPick(gap.earliest!)}
              className="absolute left-0 right-0 flex items-center justify-center rounded-button border border-dashed border-accent-line px-1 text-center hover:border-accent"
              style={{ top, height, background: FITS }}>
              <span className="text-[10.5px] font-bold leading-[1.3] text-accent-dark">
                fits from {clockBare(gap.earliest)}<br />+{gap.added} min · {GRADE_LABELS[gap.grade]}
              </span>
            </button>
          )
        }
        if (gap.free >= minutes) {
          return (
            <div key={gap.index}
              className="absolute left-0 right-0 flex items-center justify-center rounded-button text-center"
              style={{ top, height, background: HATCH }}>
              <span className="text-[10px] font-semibold leading-[1.3] text-text-faint">too short</span>
            </div>
          )
        }
        return null
      })}

      <div className="absolute left-0 right-0 border-t-[1.5px] border-dashed border-line-strong"
        style={{ top: at(axis, plan.workEnd) }} />
      <div className="absolute right-0 text-[9.5px] font-semibold text-text-faint"
        style={{ top: at(axis, plan.workEnd) + 3 }}>
        ends {clockBare(plan.workEnd)}
      </div>
    </div>
  )
}

/** *New · <property>* words for the block, from the sheet's facts. */
export function blockWords(facts: { propertyName: string | null; street: string | null; jobTitle: string | null }): NewBlockWords {
  return {
    title: facts.propertyName ?? 'New site visit',
    sub: [facts.street, facts.jobTitle ? facts.jobTitle.toLowerCase() : null].filter(Boolean).join(' · '),
  }
}

/** The day's height, for a box that has to hold it. */
export const axisHeight = (axis: Axis): number => (axis.to - axis.from) * PX
